import { io, Socket } from 'socket.io-client';

class SocketService {
  private socket: Socket | null = null;
  private token: string | null = null;
  private isConnecting: boolean = false;

  connect(token: string) {
    // Prevent multiple simultaneous connection attempts
    if (this.socket?.connected || this.isConnecting) {
      return;
    }

    // Disconnect any existing connection first
    if (this.socket) {
      this.socket.disconnect();
    }

    this.isConnecting = true;
    this.token = token;
    const serverUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';
    
    this.socket = io(serverUrl, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling']
    });

    this.socket.on('connect', () => {
      this.isConnecting = false;
      console.log('Connected to server');
    });

    this.socket.on('disconnect', () => {
      this.isConnecting = false;
      console.log('Disconnected from server');
    });

    this.socket.on('error', (error) => {
      this.isConnecting = false;
      console.error('Socket error:', error);
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnecting = false;
  }

  getSocket() {
    return this.socket;
  }

  authenticate(userId: string, first_name: string, last_name: string) {
    this.socket?.emit('authenticate', { userId, first_name, last_name });
  }

  // Meeting events
  joinMeeting(meetingId: string) {
    console.log(' [SOCKET DEBUG] Joining meeting room:', meetingId);
    this.socket?.emit('join-meeting', meetingId);
  }

  leaveMeeting(meetingId: string) {
    console.log(' [SOCKET DEBUG] Leaving meeting room:', meetingId);
    this.socket?.emit('leave-meeting', meetingId);
  }

  // Message events (socket-based sending, optional — HTTP API is preferred for persistence)
  sendMessage(meetingId: string, message: string) {
    this.socket?.emit('send-message', { 
      meetingId,
      content: message,
      type: 'text',
    });
  }

  onGroupMessage(callback: (data: any) => void) {
    this.socket?.on('group-message', callback);
  }

  onPrivateMessage(callback: (data: any) => void) {
    this.socket?.on('private-message', callback);
  }

  onMessageDeleted(callback: (data: any) => void) {
    this.socket?.on('message-deleted', callback);
  }

  // Media control events
  toggleVideo(meetingId: string, enabled: boolean) {
    this.socket?.emit('toggle-video', { meetingId, enabled });
  }

  toggleAudio(meetingId: string, enabled: boolean) {
    this.socket?.emit('toggle-audio', { meetingId, enabled });
  }

  toggleScreenShare(meetingId: string, enabled: boolean) {
    this.socket?.emit('toggle-screen-share', { meetingId, enabled });
  }

  // Typing indicators (align with backend)
  startTyping(meetingId: string) {
    this.socket?.emit('typing', { meetingId, isTyping: true });
  }

  stopTyping(meetingId: string) {
    this.socket?.emit('typing', { meetingId, isTyping: false });
  }

  onUserTyping(callback: (data: any) => void) {
    this.socket?.on('user-typing', callback);
  }

  onParticipantJoined(callback: (data: any) => void) {
    this.socket?.on('participant-joined', callback);
  }

  onParticipantLeft(callback: (data: any) => void) {
    this.socket?.on('participant-left', callback);
  }

  // Event listeners
  onUserJoined(callback: (data: any) => void) {
    this.socket?.on('user-joined', callback);
  }

  onUserLeft(callback: (data: any) => void) {
    this.socket?.on('user-left', callback);
  }

  onMessageReceived(callback: (data: any) => void) {
    this.socket?.on('message-received', callback);
  }

  onMediaToggled(callback: (data: any) => void) {
    this.socket?.on('media-toggled', callback);
  }

  onTypingStart(callback: (data: any) => void) {
    this.socket?.on('typing-start', callback);
  }

  onTypingStop(callback: (data: any) => void) {
    this.socket?.on('typing-stop', callback);
  }

  // Recording events
  onRecordingStarted(callback: (data: any) => void) {
    this.socket?.on('recording-started', callback);
  }

  onRecordingStopped(callback: (data: any) => void) {
    this.socket?.on('recording-stopped', callback);
  }

  // Remove event listeners
  off(event: string, callback?: any) {
    this.socket?.off(event, callback);
  }
}

export const socketService = new SocketService();
export default socketService;
